import { BaseComponent } from "../BaseComponent";
import * as React from "react";
import { Content } from "../Resources/Content";
import { range } from "../../Utils/Numbers";

export interface IPagerLinksProps extends React.Props<PagerLinks> {
    currentPage: number;
    numberOfPages: number;
    onChange: (pageIndex: number) => void;
    isShowFirstBtn?: boolean;
    isShowPrevBtn?: boolean;
    isShowNextBtn?: boolean;
    isShowEndBtn?: boolean;
}

export interface IPagerLinksState {}

export class PagerLinks extends BaseComponent<IPagerLinksProps, IPagerLinksState> {
    static defaultProps = {
        isShowFirstBtn: true,
        isShowPrevBtn: true,
        isShowNextBtn: true,
        isShowEndBtn: true,
    };

    constructor(props: IPagerLinksProps) {
        super(props);
        this.state = {};
    }

    private renderFirstBtn(): JSX.Element {
        const { currentPage, onChange, isShowFirstBtn } = this.props;

        if (isShowFirstBtn) {
            return (
                <button
                    type="button"
                    className="page-btn first-btn"
                    disabled={currentPage === 1}
                    aria-current="false"
                    onClick={() => onChange(1)}
                >
                    {Content.First_button} <span className="sr-only">{"firstPage"}</span>
                </button>
            );
        } else {
            return null;
        }
    }

    private renderPretBtn(): JSX.Element {
        const { currentPage, onChange, isShowPrevBtn } = this.props;
        if (isShowPrevBtn) {
            return (
                <button
                    type="button"
                    className="page-btn prev-btn"
                    aria-current="false"
                    disabled={currentPage === 1}
                    onClick={() => onChange(currentPage - 1)}
                >
                    <span aria-hidden="true" className="icon"></span>
                    {Content.Previous_button} <span className="sr-only">{Content.Page}</span>
                </button>
            );
        } else {
            return null;
        }
    }

    private renderNextBtn(): JSX.Element {
        const { currentPage, numberOfPages, onChange} = this.props;
        const isShowNextBtn = currentPage != numberOfPages;
        if (isShowNextBtn) {
            return (
                <button
                    type="button"
                    className="page-btn next-btn"
                    aria-current="false"
                    disabled={currentPage > numberOfPages}
                    onClick={() => onChange(currentPage + 1)}
                >
                    <span aria-hidden="true" className="icon"></span>
                    {Content.Next_button} <span className="sr-only">{Content.Page}</span>
                </button>
            );
        } else {
            return null;
        }
    }

    private renderEndBtn(): JSX.Element {
        const { currentPage, numberOfPages, onChange, isShowEndBtn } = this.props;
        if (isShowEndBtn) {
            return (
                <button
                    type="button"
                    className="page-btn end-btn"
                    aria-current="false"
                    disabled={currentPage === numberOfPages}
                    onClick={() => onChange(numberOfPages)}
                >
                    {Content.Last_button} <span className="sr-only">{"endpage"}</span>
                </button>
            );
        } else {
            return null;
        }
    }

    public render(): JSX.Element {
        return (
            <ul className="upload-file-list">
                <li className="pr-0">{this.renderFirstBtn()}</li>
                <li className="pr-0">{this.renderPretBtn()}</li>
                <li className="pr-0">{this.renderPagingLinks()}</li>
                <li className="pr-0">{this.renderNextBtn()}</li>
                <li className="pr-0">{this.renderEndBtn()}</li>
            </ul>
        );
    }

    private renderPagingLinks(): JSX.Element[] {
        return range(1, this.props.numberOfPages + 1).map((index) => {
            if (this.shouldPagingLinkDisplay(index, this.props.currentPage, this.props.numberOfPages)) {
                return this.renderPagingLinkElement(index, this.props.currentPage);
            } else if (this.shouldPagingDotsDisplay(index, this.props.currentPage)) {
                return this.renderPagingDotsElement(index);
            }

            return null;
        });
    }

    private isPageLinkActive(index: number, active: number): boolean {
        return index === active;
    }

    private shouldPagingLinkDisplay(index: number, active: number, total: number): boolean {
        // A = Active, T = Total
        // Assumption: Active will always be <= Total

        // 1, 2, 3
        if (index <= 3 && total >= index) {
            return true;
        }

        // A-2, A-1
        if (index >= active - 2 && index <= active - 1) {
            return true;
        }

        // A
        if (index === active) {
            return true;
        }

        // A+1, A+2
        if (index >= active + 1 && index <= active + 2 && index <= total) {
            return true;
        }

        // T-2, T-1, T
        if (index >= total - 2) {
            return true;
        }

        return false;
    }

    private renderPagingLinkElement(index: number, active: number): JSX.Element {
        const isCurrent = this.isPageLinkActive(index, active);
        const _class = "page-btn " + (isCurrent ? "current-page" : "");

        return (
            <button
                type="button"
                className={_class}
                aria-current={isCurrent ? "true" : "false"}
                key={index}
                onClick={() => this.props.onChange(index)}
            >
                {index}
            </button>
        );
    }

    private shouldPagingDotsDisplay(index: number, active: number): boolean {
        // The dots are displayed at a specific index only
        return index === active - 3 || index === active + 3;
    }

    private renderPagingDotsElement(index: number): JSX.Element {
        return (
            <button type="button" className="page-btn" disabled={true} key={index} aria-current="false">
                &hellip;
            </button>
        );
    }
}
